import React, { useState, useEffect } from 'react';
import { localAgentService } from '../../services/localAgentService';
import { createOrdersService } from '../../services/ordersService';
import { useAuthBusiness } from '../../contexts/AuthBusinessContext';
import {
  ServerStackIcon,
  CloudIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XMarkIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';

export default function LocalAgentStatusBadge({ className = '', variant = 'default', isCollapsed = false }) {
  const { selectedBusiness } = useAuthBusiness();
  const [agentState, setAgentState] = useState({
    status: 'unknown',
    health: null,
    baseUrl: 'http://localhost:4005'
  });
  const [isBrowserOnline, setIsBrowserOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [showModal, setShowModal] = useState(false);
  const [ipInput, setIpInput] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  useEffect(() => {
    // Subscribe to local agent service updates
    const unsubscribe = localAgentService.subscribe((state) => {
      setAgentState(state);
    });

    setIpInput(localAgentService.getCashRegisterIp());

    // Browser network listeners
    const handleOnline = () => setIsBrowserOnline(true);
    const handleOffline = () => setIsBrowserOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Load initial pending offline orders count
    const checkPending = async () => {
      if (selectedBusiness?.id) {
        const count = await localAgentService.getPendingCount(selectedBusiness.id);
        setPendingCount(count);
      }
    };
    checkPending();
    const interval = setInterval(checkPending, 8000);

    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [selectedBusiness?.id]);

  const handleSaveIp = (e) => {
    e.preventDefault();
    localAgentService.setCashRegisterIp(ipInput);
    setSyncMessage({ type: 'success', text: 'Kasa IP adresi kaydedildi. Bağlantı test ediliyor...' });
    setTimeout(() => setSyncMessage(null), 3500);
  };

  const handleManualSync = async () => {
    if (!selectedBusiness?.id || isSyncing) return;
    setIsSyncing(true);
    setSyncMessage(null);

    try {
      const ordersService = createOrdersService(selectedBusiness.id);
      const res = await ordersService.syncOfflineOrders();
      if (res.success) {
        setSyncMessage({
          type: 'success',
          text: res.total > 0
            ? `${res.synced} adet çevrimdışı sipariş başarıyla buluta aktarıldı.`
            : 'Aktarılacak bekleyen çevrimdışı sipariş bulunmuyor.'
        });
        const count = await localAgentService.getPendingCount(selectedBusiness.id);
        setPendingCount(count);
      } else {
        setSyncMessage({ type: 'error', text: 'Senkronizasyon başarısız oldu: ' + (res.error || 'Ağ hatası') });
      }
    } catch (err) {
      setSyncMessage({ type: 'error', text: 'Hata: ' + err.message });
    } finally {
      setIsSyncing(false);
    }
  };

  // Determine appearance
  const isAgentOnline = agentState.status === 'online';

  let badgeConfig = {
    bg: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
    dot: 'bg-emerald-500 animate-pulse',
    icon: ServerStackIcon,
    label: 'Yerel Kasa Modu',
    title: 'Ana kasa bağlantısı aktif. Menü ve masalar yerel cihazdan anında alınıyor.'
  };

  if (!isBrowserOnline) {
    badgeConfig = {
      bg: 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100',
      dot: 'bg-rose-500 animate-ping',
      icon: ExclamationTriangleIcon,
      label: pendingCount > 0 ? `Çevrimdışı (${pendingCount} Bekleyen)` : 'Çevrimdışı',
      title: 'İnternet bağlantısı kesildi. Siparişleriniz yerel hafızaya güvenle kaydediliyor.'
    };
  } else if (!isAgentOnline) {
    badgeConfig = {
      bg: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
      dot: 'bg-blue-400',
      icon: CloudIcon,
      label: pendingCount > 0 ? `Bulut Modu (${pendingCount} Bekleyen)` : 'Bulut Modu',
      title: 'Merkezi bulut sistemi üzerinden canlı çalışılıyor.'
    };
  }

  const BadgeIcon = badgeConfig.icon;

  return (
    <>
      {variant === 'sidebar' ? (
        isCollapsed ? (
          // 🚪 Sidebar KAPALI Durumu: İkonik Minik Buton & Sağa Açılan Tooltip
          <div className="relative group w-full flex justify-center py-2">
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all cursor-pointer border ${
                !isBrowserOnline 
                  ? 'bg-rose-950/40 border-rose-500/40 text-rose-400 hover:bg-rose-900/60' 
                  : isAgentOnline 
                    ? 'bg-emerald-950/40 border-emerald-500/40 text-emerald-400 hover:bg-emerald-900/60'
                    : 'bg-blue-950/40 border-blue-500/40 text-blue-400 hover:bg-blue-900/60'
              }`}
              title={badgeConfig.title}
            >
              <span className="relative flex h-2.5 w-2.5">
                {!isBrowserOnline && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                )}
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                  !isBrowserOnline ? 'bg-rose-500' : isAgentOnline ? 'bg-emerald-400' : 'bg-blue-400'
                }`}></span>
              </span>
            </button>

            {/* Sağa Açılan Tooltip */}
            <div className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 hidden group-hover:flex flex-col whitespace-nowrap px-3 py-1.5 bg-slate-900/95 text-white border border-slate-700 rounded-xl shadow-2xl backdrop-blur-md z-[100001]">
              <div className="flex items-center gap-1.5 font-bold text-[11px]">
                <span className={`w-2 h-2 rounded-full ${
                  !isBrowserOnline ? 'bg-rose-500' : isAgentOnline ? 'bg-emerald-400' : 'bg-blue-400'
                }`}></span>
                <span className={!isBrowserOnline ? 'text-rose-400' : isAgentOnline ? 'text-emerald-400' : 'text-blue-400'}>
                  {!isBrowserOnline ? 'Çevrimdışı (Yerel Hafıza)' : isAgentOnline ? 'Yerel Kasa Modu' : 'Merkezi Bulut Modu'}
                </span>
              </div>
              <span className="text-[9px] text-slate-400">Durumu görmek için tıkla</span>
            </div>
          </div>
        ) : (
          // 🚪 Sidebar AÇIK Durumu: Şık Alt Durum Kartı
          <div className="px-3 py-2 w-full">
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className={`w-full flex items-center justify-between p-2.5 rounded-2xl border transition-all cursor-pointer text-left group ${
                !isBrowserOnline 
                  ? 'bg-rose-950/30 border-rose-500/30 hover:bg-rose-950/50' 
                  : isAgentOnline 
                    ? 'bg-emerald-950/30 border-emerald-500/30 hover:bg-emerald-950/50'
                    : 'bg-blue-950/30 border-blue-500/30 hover:bg-blue-950/50'
              }`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="relative flex h-2.5 w-2.5 flex-none">
                  {!isBrowserOnline && (
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                  )}
                  <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                    !isBrowserOnline ? 'bg-rose-500' : isAgentOnline ? 'bg-emerald-400' : 'bg-blue-400'
                  }`}></span>
                </span>
                <div className="flex flex-col min-w-0">
                  <span className={`text-[11px] font-black leading-tight truncate ${
                    !isBrowserOnline ? 'text-rose-400' : isAgentOnline ? 'text-emerald-400' : 'text-blue-400'
                  }`}>
                    {!isBrowserOnline ? 'Çevrimdışı Mod' : isAgentOnline ? 'Yerel Kasa Modu' : 'Merkezi Buluta Bağlı'}
                  </span>
                  <span className="text-[9px] text-slate-400 font-semibold truncate">
                    {!isBrowserOnline ? 'Yerel hafıza aktif' : 'Canlı senkronize'}
                  </span>
                </div>
              </div>

              {pendingCount > 0 ? (
                <span className="px-1.5 py-0.5 bg-amber-500/20 border border-amber-500/40 text-amber-300 rounded-full text-[9px] font-black">
                  {pendingCount}
                </span>
              ) : (
                <Cog6ToothIcon className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors flex-none" />
              )}
            </button>
          </div>
        )
      ) : (
        // Standart Header / POS Rozeti
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className={`px-2.5 py-1.5 rounded-xl border text-[11px] font-black flex items-center gap-1.5 transition-all shadow-xs select-none cursor-pointer active:scale-95 ${badgeConfig.bg} ${className}`}
          title={badgeConfig.title}
        >
          <span className={`w-2 h-2 rounded-full ${badgeConfig.dot}`}></span>
          <BadgeIcon className="w-3.5 h-3.5 flex-none" />
          <span className="hidden sm:inline">{badgeConfig.label}</span>
        </button>
      )}

      {/* Connection & Offline Queue Drawer/Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-[100000] p-4 animate-in fade-in duration-150">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-5 flex flex-col gap-4 border border-gray-100">
            <div className="flex items-center justify-between border-b pb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                  <ServerStackIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-gray-900">Kasa & Çevrimdışı Çalışma Durumu</h3>
                  <p className="text-[10px] text-gray-500">Yerel Kasa Cihazı ve Bulut Senkronizasyonu</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 cursor-pointer"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Durum Kartları */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="p-3 bg-gray-50 rounded-2xl border border-gray-100">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Yerel Kasa Bağlantısı</span>
                <div className="flex items-center gap-1.5 font-black">
                  <span className={`w-2.5 h-2.5 rounded-full ${isAgentOnline ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                  <span className={isAgentOnline ? 'text-emerald-700' : 'text-gray-600'}>
                    {isAgentOnline ? 'BAĞLI (AKTİF)' : 'DEVRE DIŞI'}
                  </span>
                </div>
                <span className="text-[9px] text-gray-400 block mt-1 font-mono truncate">
                  {isAgentOnline ? 'Yerel Cihaz Bağlı' : 'Bulut Modu Devrede'}
                </span>
              </div>

              <div className="p-3 bg-gray-50 rounded-2xl border border-gray-100">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Merkezi Bulut Bağlantısı</span>
                <div className="flex items-center gap-1.5 font-black">
                  <span className={`w-2.5 h-2.5 rounded-full ${isBrowserOnline ? 'bg-emerald-500' : 'bg-rose-500 animate-pulse'}`}></span>
                  <span className={isBrowserOnline ? 'text-emerald-700' : 'text-rose-700'}>
                    {isBrowserOnline ? 'BULUTA BAĞLI' : 'ÇEVRİMDIŞI'}
                  </span>
                </div>
                <span className="text-[9px] text-gray-400 block mt-1">
                  {isBrowserOnline ? 'Canlı senkronizasyon aktif' : 'Siparişler yerel hafızada'}
                </span>
              </div>
            </div>

            {/* Bekleyen Çevrimdışı Siparişler Bölümü */}
            <div className="p-3.5 bg-amber-50/80 border border-amber-200 rounded-2xl">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 flex-none" />
                  <span className="text-xs font-black text-amber-900">Bekleyen Çevrimdışı Siparişler</span>
                </div>
                <span className="px-2 py-0.5 bg-amber-200/80 text-amber-900 rounded-full text-[10px] font-black">
                  {pendingCount} Bekleyen
                </span>
              </div>
              <p className="text-[10px] text-amber-800 mb-3">
                İnternet bağlantısı kesildiğinde aldığınız siparişler yerel hafızada güvenle saklanır; internet geldiğinde otomatik olarak merkezi sisteme aktarılır.
              </p>
              <button
                type="button"
                onClick={handleManualSync}
                disabled={isSyncing || pendingCount === 0}
                className="w-full py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-xs cursor-pointer"
              >
                <ArrowPathIcon className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                <span>{isSyncing ? 'Eşitleniyor...' : 'Şimdi Buluta Aktar'}</span>
              </button>
            </div>

            {syncMessage && (
              <div className={`p-2.5 rounded-xl text-xs font-bold text-center ${
                syncMessage.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-rose-50 text-rose-800 border border-rose-200'
              }`}>
                {syncMessage.text}
              </div>
            )}

            {/* Garson Cihazı için Kasa IP Ayarı */}
            <form onSubmit={handleSaveIp} className="p-3.5 bg-gray-50 rounded-2xl border border-gray-100 space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-black text-gray-800">
                <Cog6ToothIcon className="w-4 h-4 text-gray-500" />
                <span>El Terminali / Garson Cihazı Bağlantısı</span>
              </div>
              <p className="text-[10px] text-gray-500">
                Garson telefonu veya tablet kullanıyorsanız, ana kasanın yerel Wi-Fi IP adresini (Örn: <code className="text-blue-600">192.168.1.105</code>) girerek kasaya doğrudan bağlanabilirsiniz. Ana kasa bilgisayarında boş bırakınız.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Örn: 192.168.1.100 (Boş = Yerel Cihaz)"
                  value={ipInput}
                  onChange={(e) => setIpInput(e.target.value)}
                  className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-mono font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 outline-none"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer"
                >
                  Kaydet
                </button>
              </div>
            </form>

            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-all"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
